import { createSelector } from 'reselect';
import last from 'lodash/last';
import memoize from 'lodash/memoize';
import { ISSUE_HIERARCHY_LEVEL_EPIC } from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
import type { Filters } from '@atlassian/jira-software-filters/src/common/types.tsx';
import {
	createDefaultMatcherCreators,
	createFilterFunction,
} from '@atlassian/jira-software-filters/src/utils/index.tsx';
import type {
	FilterableIssue,
	Matcher,
	Context,
} from '@atlassian/jira-software-filters/src/utils/types.tsx';
import {
	ASSIGNEE,
	CUSTOM_FILTER,
	ISSUE_PARENT,
	ISSUE_TYPE,
	LABEL,
	VERSION,
} from '../../../model/filters/constants.tsx';
import type { Card } from '../../../model/types.tsx';
import type { State } from '../../types.tsx';
import { getEntities } from '../entities/index.tsx';

export const getFilters = (state: State) => state.ui.filters;

export const getCMPFilters = (state: State) => getEntities(state).customFilters.filters;

export const getAppliedParentFilters = (state: State) => getFilters(state)[ISSUE_PARENT];

export const getAppliedAssigneeFilters = (state: State) => getFilters(state)[ASSIGNEE];

export const getAppliedVersionFilters = (state: State) => getFilters(state)[VERSION];

export const getAppliedLabelFilters = (state: State) => getFilters(state)[LABEL];

export const getAppliedCustomFilters = (state: State) => getFilters(state)[CUSTOM_FILTER];

export const getAppliedCustomFilterQueries = createSelector(
	[getCMPFilters, getAppliedCustomFilters],
	(filters, currentAppliedFilters) =>
		filters
			.filter((f) => currentAppliedFilters?.includes(f.id))
			.map((f) => f.query)
			.filter(Boolean),
);

export const getAppliedIssueTypeFilters = (state: State): undefined | string[] =>
	getFilters(state)[ISSUE_TYPE];

export const getLastFilteredIssueTypeId = createSelector(
	[getAppliedIssueTypeFilters],
	(issueTypes) => {
		if (issueTypes && issueTypes.length) {
			return Number(last(issueTypes));
		}

		return undefined;
	},
);

export const isIssueIdOptimistic: Matcher<FilterableIssue> = (issue) => Number(issue.id) < 0;

export const filter = (filterValues: Filters, filteredIssueIds?: Set<string>, cards?: Card[]) => {
	const toFilterableIssue = memoize(
		({
			id,
			key,
			summary,
			labels,
			typeId,
			parentId,
			assignee,
			fixVersions,
		}: Card): FilterableIssue => {
			const filterableAssignee = assignee
				? {
						id: assignee.id,
						displayName: assignee.displayName,
					}
				: undefined;

			return {
				id: String(id),
				key,
				summary,
				labels,
				type: { id: String(typeId) },
				versionIds: fixVersions,
				parentId:
					typeof parentId !== 'undefined' && parentId !== null ? String(parentId) : undefined,
				assignee: filterableAssignee,
			};
		},
	);

	const issuesContext: Context['issues'] =
		cards && cards.length ? { list: [], hash: {} } : undefined;

	if (issuesContext) {
		cards
			?.filter((card) => card.typeHierarchyLevel !== ISSUE_HIERARCHY_LEVEL_EPIC)
			.forEach((card) => {
				const filterableIssue = toFilterableIssue(card);
				issuesContext.list.push(filterableIssue);
				issuesContext.hash[card.id] = filterableIssue;
			});
	}

	const filterFunction = createFilterFunction(
		filterValues,
		{ filteredIssueIds, issues: issuesContext },
		createDefaultMatcherCreators<FilterableIssue>(),
		[isIssueIdOptimistic],
	);

	return (issues: Card[]): Card[] =>
		issues.filter((issue) => {
			const filterableIssue = toFilterableIssue(issue);
			return filterFunction(filterableIssue);
		});
};
